import {useEffect, useRef, useState, type ReactNode} from 'react';
import {Image} from 'lucide-react';
import {Card, CardContent, CardFooter} from '@kroll-resolver-crisp/ui-components-react';
import {CopyableUrl, CopyUrlButton} from '@/components/shared/CopyableUrl';
import {
  usePinterestProfileStatus,
  usePinterestPinStatus,
  useExistingSubmissions,
  useClassificationConfig,
  useLanguages,
  useTopics,
  useLocations
} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {type Item} from '@/api/items';
import {type Submission} from '@/api/submissions';
import {groupSubmissionsByStatus, worstStatusBorderClassName} from '@/lib/submissionStatusStyle';
import {buildPinterestProfileMetadataFields} from '@/lib/pinterestItemMetadata';
import {buildPinItem} from '@/lib/pinterestPinItem';
import {type PinterestPinStatus, type PinterestProfileStatus} from '@/api/pinterest';
import {
  parsePinterestPinInput,
  parsePinterestProfileInput,
  type ParsedPinterestProfile
} from '@/lib/pinterest';
import {PLATFORM_ID, DEFAULT_AFFILIATE} from '@/lib/platform';
import {PinterestProfileCard} from '@/components/channels/PinterestProfileCard';
import {
  ResolvedRow,
  ResolvedSection,
  RowActions,
  SubmissionInputSection,
  SubmissionPageShell
} from '@/components/submissions/SubmissionForm';
import {
  CreateP0AlertDialog,
  useP0AlertDialog,
  type P0LinkedItem
} from '@/components/p0alerts/CreateP0AlertDialog';
import {useDismissedKeys} from '@/components/submissions/useDismissedKeys';
import {ClassifyDialog} from '@/components/investigations/ClassifyDialog';
import {usePendingFlags} from '@/components/submissions/usePendingFlags';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';
import {cn} from '@/lib/utils';

type ResolvedProfile = {
  key: string;
  raw: string;
  parsed: ParsedPinterestProfile | null;
  loading: boolean;
  error: string | null;
  status: PinterestProfileStatus | null;
  // Set when the line was a pin URL or id: the row is then the pin itself and is submitted as a
  // Content submission keyed on the pin id. status is the pin's owner, kept only for the "posted by"
  // line and the flag label - it is not what gets submitted.
  pin: PinterestPinStatus | null;
  submitted: boolean;
  existingSubmissions: Submission[];
};

function splitLines(text: string): string[] {
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const rawLine of text.split('\n')) {
    const trimmed = rawLine.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      lines.push(trimmed);
    }
  }

  return lines;
}

// The one identity every part of this page must agree on: Pinterest's own numeric user_id, which
// is what the pinterest_profile spider reports as AUTHOR_ID/ContentId, with the handle as a
// best-effort fallback (see PinterestProfileStatus.user_id). Submitting under one key and matching
// under another silently breaks the duplicate guard and the submitted state, so all three call
// sites go through here.
const profileIdentity = (status: PinterestProfileStatus) => status.user_id ?? status.username;

// The pin's owner, as the profile shape the row's flag label and "posted by" line use. A pin is
// submitted as itself (see buildPinItem), never as its owner.
function ownerStatusFromPin(pin: PinterestPinStatus): PinterestProfileStatus {
  const {owner} = pin;
  return {
    username: owner.username,
    display_name: owner.display_name,
    avatar_url: owner.avatar_url,
    profile_url: owner.profile_url,
    bio: owner.bio,
    follower_count: owner.follower_count,
    following_count: null,
    pin_count: owner.pin_count,
    board_count: null,
    created_at: null,
    website_url: null,
    is_private_profile: null,
    is_verified_merchant: null,
    user_id: owner.user_id
  };
}

function PinPreview({
  pin,
  borderClassName,
  footer
}: {
  readonly pin: PinterestPinStatus;
  readonly borderClassName?: string;
  // The pin's own actions (flag, classify, or the submitted state), rendered in the card footer.
  readonly footer?: ReactNode;
}) {
  return (
    <Card className={cn('border', borderClassName ?? 'border-border')}>
      <CardContent className="flex items-start gap-4">
        {pin.image_url ? (
          <div className="group bg-card relative size-24 shrink-0 overflow-hidden rounded-md">
            <SensitiveMedia compact src={pin.image_url} alt="" className="size-full object-cover" />
          </div>
        ) : null}
        <div className="flex min-w-0 flex-col gap-1 text-xs">
          <div className="text-muted-foreground font-medium">Pin - submitting this pin</div>
          <div className="text-muted-foreground">
            Posted by <span className="text-foreground font-medium">@{pin.owner.username}</span>
            {pin.owner.profile_url ? (
              <CopyUrlButton
                url={pin.owner.profile_url}
                label="Copy profile URL"
                className="ml-0.5 align-middle"
              />
            ) : null}
          </div>
          {pin.description ? <p className="line-clamp-3">{pin.description}</p> : null}
          <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
            {pin.board_name ? <span>Board: {pin.board_name}</span> : null}
            {pin.save_count === null ? null : <span>{pin.save_count.toLocaleString()} saves</span>}
            {pin.domain ? <span>{pin.domain}</span> : null}
            {pin.url ? <CopyableUrl url={pin.url} label="Copy pin URL" /> : null}
          </div>
        </div>
      </CardContent>
      {footer ? <CardFooter className="justify-end gap-2">{footer}</CardFooter> : null}
    </Card>
  );
}

function buildItemForProfile(profile: ResolvedProfile): Item | null {
  // A pin row is submitted as the pin itself (a Content submission), not as its owner.
  if (profile.pin) {
    return buildPinItem(profile.pin, groupSubmissionsByStatus(profile.existingSubmissions));
  }

  if (!profile.status) {
    return null;
  }

  const s = profile.status;
  // Content_type: 'actor' is what makes ClassifyDialog derive submission_type: 'Actor' for this item.
  // external_id/author_id key on Pinterest's numeric user_id, not the handle - see
  // profileIdentity above.
  const identity = profileIdentity(s);
  return {
    id: crypto.randomUUID(),
    external_id: identity,
    platform: PLATFORM_ID,
    content_type: 'actor',
    title: s.display_name ?? s.username,
    url: s.profile_url,
    thumbnail_url: s.avatar_url,
    author_handle: s.username,
    author_id: identity,
    author_name: s.display_name,
    published_at: null,
    metadata: {Fields: buildPinterestProfileMetadataFields(s)},
    affiliate: DEFAULT_AFFILIATE,
    created_at: new Date().toISOString(),
    submission_status: null,
    submission_policy_violations: [],
    // Reshaped from the same existingSubmissions probe the badge below already renders, so
    // ClassifyDialog's same-vertical resubmit detection works for this fetched-live profile too
    // (see groupSubmissionsByStatus's own comment).
    submission_status_groups: groupSubmissionsByStatus(profile.existingSubmissions),
    item_status: null,
    is_new_item: null
  };
}

// Pinterest has no live status API the way YouTube's Data API does, but its own profile page
// carries enough embedded structured data (name, username, a real avatar image) to fetch live and
// show immediately - see PinterestProfileStatusClient. Classifying from the resolved card builds
// the item client-side and submits directly, exactly like SingleVideoSubmissionPage's channel path;
// no async job needs to finish first. This page is purely the actor-submission workflow - profile
// (and pin/board/follower) collection, and image search, both live in Investigations now that it's
// unhidden on Falcon (see NewInvestigationDialog's platform-aware tabs).
export function PinterestSubmissionPage() {
  const p0Dialog = useP0AlertDialog();
  const dismissed = useDismissedKeys();
  const [bulkInput, setBulkInput] = useState('');
  const [profiles, setProfiles] = useState<ResolvedProfile[]>([]);
  const profilesRef = useRef<ResolvedProfile[]>([]);
  const [classifyItems, setClassifyItems] = useState<Item[]>([]);
  const pendingFlags = usePendingFlags((key) => {
    const profile = profilesRef.current.find((p) => p.key === key);
    return profile ? buildItemForProfile(profile) : null;
  });

  const profileStatus = usePinterestProfileStatus();
  const pinStatus = usePinterestPinStatus();
  const existingSubmissionsCheck = useExistingSubmissions();

  const {data: classifications = []} = useClassificationConfig();
  const {data: languages = []} = useLanguages();
  const {data: topics = []} = useTopics();
  const {data: locations = []} = useLocations();

  useEffect(() => {
    profilesRef.current = profiles;
  }, [profiles]);

  const updateProfile = (key: string, patch: Partial<ResolvedProfile>) => {
    setProfiles((previous) => previous.map((p) => (p.key === key ? {...p, ...patch} : p)));
  };

  // Must look up the SAME identity buildItemForProfile submits under (the numeric user_id), or the
  // badge silently never matches and an already-submitted profile looks unsubmitted. The handle is
  // checked as well, not instead: submissions created before the switch to user_id are keyed on it.
  const checkExisting = async (key: string, externalIds: string[]) => {
    try {
      const results = await Promise.all(
        [...new Set(externalIds)].map(async (externalId) =>
          existingSubmissionsCheck.mutateAsync({platform: PLATFORM_ID, externalId})
        )
      );
      const byId = new Map(results.flat().map((submission) => [submission.id, submission]));
      updateProfile(key, {existingSubmissions: [...byId.values()]});
    } catch {
      // Silently skip - absence of the badge just means "unknown", not "not submitted".
    }
  };

  const resolveProfile = async (key: string, parsed: ParsedPinterestProfile) => {
    try {
      const status = await profileStatus.mutateAsync(parsed);
      updateProfile(key, {status, loading: false, error: null});
      void checkExisting(key, [profileIdentity(status), parsed.username]);
    } catch (error) {
      updateProfile(key, {
        loading: false,
        error: getApiErrorMessage(error, 'Profile not found')
      });
    }
  };

  const resolvePin = async (key: string, pinId: string) => {
    try {
      const pin = await pinStatus.mutateAsync(pinId);
      updateProfile(key, {pin, status: ownerStatusFromPin(pin), loading: false, error: null});
      // A pin submission is keyed on the pin id.
      void checkExisting(key, [pin.pin_id]);
    } catch (error) {
      updateProfile(key, {
        loading: false,
        error: getApiErrorMessage(error, 'Pin not found')
      });
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      const raws = splitLines(bulkInput);
      const prevByKey = new Map(profilesRef.current.map((p) => [p.key, p]));
      const next: ResolvedProfile[] = [];
      const toResolve: Array<{key: string; parsed: ParsedPinterestProfile}> = [];
      const pinsToResolve: Array<{key: string; pinId: string}> = [];

      for (const raw of raws) {
        const pinId = parsePinterestPinInput(raw);
        if (pinId) {
          const pinKey = `pin:${pinId}`;
          const existingPin = prevByKey.get(pinKey);
          if (existingPin) {
            next.push(existingPin);
            continue;
          }

          next.push({
            key: pinKey,
            raw,
            parsed: null,
            loading: true,
            error: null,
            status: null,
            pin: null,
            submitted: false,
            existingSubmissions: []
          });
          pinsToResolve.push({key: pinKey, pinId});
          continue;
        }

        const parsed = parsePinterestProfileInput(raw);
        const key = parsed ? `${parsed.locale}:${parsed.username}` : `unknown:${raw}`;
        const existing = prevByKey.get(key);
        if (existing) {
          next.push(existing);
          continue;
        }

        if (!parsed) {
          next.push({
            key,
            raw,
            parsed: null,
            loading: false,
            error: 'Not a recognizable Pinterest profile or pin URL',
            status: null,
            pin: null,
            submitted: false,
            existingSubmissions: []
          });
          continue;
        }

        next.push({
          key,
          raw,
          parsed,
          loading: true,
          error: null,
          status: null,
          pin: null,
          submitted: false,
          existingSubmissions: []
        });
        toResolve.push({key, parsed});
      }

      setProfiles(next);
      for (const {key, parsed} of toResolve) {
        void resolveProfile(key, parsed);
      }

      for (const {key, pinId} of pinsToResolve) {
        void resolvePin(key, pinId);
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkInput]);

  const classifiableProfiles = profiles.filter(
    (p) => !p.submitted && !dismissed.isDismissed(p.key) && p.status
  );

  const classifiableKeys = classifiableProfiles.map((p) => p.key);

  // A profile or pin flagged earlier is already a real item; classifying it must land on that one.
  const openClassify = (toClassify: ResolvedProfile[]) => {
    // One row per subject, so the same profile or pin pasted twice is not classified twice.
    const seenIdentities = new Set<string>();
    const rows = toClassify.flatMap((profile) => {
      const item = buildItemForProfile(profile);
      if (!item || seenIdentities.has(item.external_id)) {
        return [];
      }

      seenIdentities.add(item.external_id);
      return [{...item, id: pendingFlags.itemIdOf(profile.key) ?? item.id}];
    });
    if (rows.length > 0) {
      setClassifyItems(rows);
    }
  };

  const handleClassifyOne = (profile: ResolvedProfile) => {
    openClassify([profile]);
  };

  const handleClassifyAll = () => {
    openClassify(classifiableProfiles);
  };

  const handleSubmitted = (
    succeededItems: Item[],
    linkedItems: P0LinkedItem[],
    wantP0: boolean
  ) => {
    const ids = new Set(succeededItems.map((i) => i.external_id));
    // A pin row is identified by its pin id, a profile row by its owner identity.
    setProfiles((previous) =>
      previous.map((p) => {
        const identity = p.pin ? p.pin.pin_id : p.status ? profileIdentity(p.status) : null;
        return identity && ids.has(identity) ? {...p, submitted: true} : p;
      })
    );
    if (wantP0) {
      p0Dialog.launch(linkedItems);
    }
  };

  const resolvedCount = profiles.filter((p) => p.status).length;
  // A pin line has no parsed profile either, so count the lines that matched neither a profile nor a pin.
  const unrecognisedCount = profiles.filter((p) => p.key.startsWith('unknown:')).length;

  // The row's own actions, rendered inside its card's footer.
  const rowActions = (profile: ResolvedProfile) => (
    <RowActions
      submitted={profile.submitted}
      flag={pendingFlags.flagOf(profile.key)}
      note={pendingFlags.noteOf(profile.key)}
      subject={profile.pin ? 'this pin' : (profile.status?.username ?? profile.raw)}
      dismissed={dismissed.isDismissed(profile.key)}
      onFlag={(color, note) => {
        pendingFlags.setFlag(profile.key, color, note);
      }}
      onNote={(note) => {
        pendingFlags.setNote(profile.key, note);
      }}
      onClassify={() => {
        handleClassifyOne(profile);
      }}
      onDismiss={() => {
        dismissed.dismiss(profile.key);
      }}
      onRestore={() => {
        dismissed.restore(profile.key);
      }}
    />
  );

  return (
    <SubmissionPageShell>
      <SubmissionInputSection
        title="Submit profiles and pins"
        description="Paste one or many Pinterest profile URLs, usernames, pin URLs or pin ids, one per line. A profile is submitted as the profile and a pin as the pin itself; classify them individually or all at once."
        platformLabel="Pinterest"
        platformIcon={<Image />}
        inputLabel="Pinterest profile / pin URLs or IDs"
        placeholder={
          'Examples:\nhttps://uk.pinterest.com/someuser/\nsomeuser\nhttps://www.pinterest.com/pin/1234567890/\n424605071146015192'
        }
        idleHint="One per line - profile URLs, usernames, pin URLs and pin ids are all accepted."
        value={bulkInput}
        hasLines={profiles.length > 0}
        resolvedCount={resolvedCount}
        unrecognisedCount={unrecognisedCount}
        pendingFlags={pendingFlags}
        classifiableKeys={classifiableKeys}
        flagAllSubject="every row"
        onValueChange={setBulkInput}
        onClassifyAll={handleClassifyAll}
      />

      {profiles.length > 0 ? (
        <ResolvedSection
          title="Resolved profiles and pins"
          summary={`${profiles.length} ${profiles.length === 1 ? 'line' : 'lines'}`}>
          {profiles.map((profile) => (
            <ResolvedRow
              key={profile.key}
              raw={profile.raw}
              loading={profile.loading}
              error={profile.error}
              existingSubmissions={profile.existingSubmissions}
              dismissed={dismissed.isDismissed(profile.key)}>
              {profile.pin ? (
                <PinPreview
                  pin={profile.pin}
                  borderClassName={worstStatusBorderClassName(
                    profile.existingSubmissions.map((s) => s.action_status)
                  )}
                  footer={rowActions(profile)}
                />
              ) : profile.status ? (
                <PinterestProfileCard
                  profile={profile.status}
                  borderClassName={worstStatusBorderClassName(
                    profile.existingSubmissions.map((s) => s.action_status)
                  )}
                  priorSubmissionCount={profile.existingSubmissions.length}
                  footer={rowActions(profile)}
                />
              ) : null}
            </ResolvedRow>
          ))}
        </ResolvedSection>
      ) : null}

      <ClassifyDialog
        items={classifyItems}
        classifications={classifications}
        languages={languages}
        topics={topics}
        locations={locations}
        origin="single_submission"
        onClose={() => {
          setClassifyItems([]);
        }}
        onStartNewBatch={() => {
          setBulkInput('');
          setProfiles([]);
          dismissed.clear();
        }}
        onSubmitted={handleSubmitted}
      />

      <CreateP0AlertDialog {...p0Dialog.dialogProps()} />
    </SubmissionPageShell>
  );
}
